import { FC, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { FormControl } from '@mui/material'

import { SelectMenu, SelectMenuProps } from '../../../../../components/SelectMenu/SelectMenu'
import { TrackedGameBlueprint } from '../../../../../features/live-events/types/TrackedGame'
import { BlueprintFamily } from '../../../../api/markets'
import { gameBlueprintSelectableForEventType } from '../../helpers/acceptedEventTypes'
import { useLiveEventsTrackingUserLanguage } from '../../hooks/useLiveEventsTrackingUserLanguage'

/**
 * Props for the EventBlueprintSelectMenu component.
 * Extends SelectMenuProps but omits 'label' and 'options' as they are handled internally.
 */
type EventBlueprintSelectMenuProps = Omit<SelectMenuProps<string>, 'label' | 'options'> & {
  /** The currently selected blueprint ID */
  value: string
  /** Callback function called when a blueprint is selected */
  onChange: (blueprintId: string) => void
  /** Array of available game blueprints to display in the dropdown */
  blueprints: TrackedGameBlueprint[]
  /** The event's type. With none chosen, every blueprint stays unmarked. */
  eventTypeId?: string
  /** Blueprint families, used to tell which game blueprints accept the event's type. */
  blueprintFamilies?: BlueprintFamily[]
}

/**
 * A select menu component for choosing game blueprints in live events tracking.
 *
 * This component renders a dropdown menu that displays available game blueprints
 * sorted alphabetically by name. The blueprint names are displayed in the user's
 * preferred language, falling back to English if the translation is not available.
 * Includes a "None" option to clear the selection.
 *
 * A blueprint whose blueprint family accepts a fixed set of event types, and not the event's
 * type, stays selectable but is shown in red so the mismatch is obvious before save.
 *
 * @param props - The component props
 * @param props.value - The currently selected blueprint ID
 * @param props.onChange - Callback function called when a blueprint is selected
 * @param props.blueprints - Array of available game blueprints
 * @param props.eventTypeId - The event's type, used to mark blueprints that do not accept it
 * @param props.blueprintFamilies - Blueprint families carrying the accepted event types
 * @param props.selectMenuProps - Additional props to pass to the underlying SelectMenu component
 *
 * @returns A FormControl wrapped SelectMenu component for blueprint selection
 */
export const EventBlueprintSelectMenu: FC<EventBlueprintSelectMenuProps> = ({
  value,
  onChange,
  blueprints,
  eventTypeId,
  blueprintFamilies,
  ...selectMenuProps
}) => {
  const userLanguage = useLiveEventsTrackingUserLanguage()
  const { t } = useTranslation()

  /**
   * Memoized options for the select menu, derived from the blueprints array.
   * Each option contains the blueprint ID as value and the localized name as display text.
   * Options are sorted alphabetically by name.
   * Includes a "None" option at the beginning to allow clearing the selection.
   */
  const blueprintOptions = useMemo(() => {
    const blueprintOptionsList =
      blueprints
        .map((blueprint) => {
          const acceptsEventType = gameBlueprintSelectableForEventType(blueprint, eventTypeId, blueprintFamilies)
          return {
            value: blueprint.blueprintId,
            name: blueprint.names[userLanguage] || blueprint.names.en || blueprint.blueprintId,
            danger: !acceptsEventType,
          }
        })
        .sort((a, b) => a.name?.localeCompare(b.name)) || []

    // Add "None" option at the beginning
    return [{ value: '', name: t('internal-common:none') }, ...blueprintOptionsList]
  }, [blueprints, eventTypeId, blueprintFamilies, t, userLanguage])

  return (
    <FormControl size="small" variant="outlined" fullWidth>
      <SelectMenu
        {...selectMenuProps}
        options={blueprintOptions}
        onChange={(e) => onChange(e.target.value)}
        value={value}
        label={<Trans i18nKey="internal-live-events:event_blueprint" />}
      />
    </FormControl>
  )
}
